/**
 * Storage utilities for managing browser storage
 * Uses sessionStorage for sensitive data to reduce XSS risk
 */

import { logApp } from './logger';

export type StorageType = 'session' | 'local';

/**
 * Gets a value from storage
 * @param key - Storage key
 * @param storageType - Type of storage to use (default: session for security)
 * @returns The stored value or null if not found
 */
export const getStorageItem = (key: string, storageType: StorageType = 'session'): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    return storage.getItem(key);
  } catch (error) {
    logApp.error(`Failed to get item from ${storageType}Storage:`, error);
    return null;
  }
};

/**
 * Sets a value in storage
 * @param key - Storage key
 * @param value - Value to store
 * @param storageType - Type of storage to use (default: session for security)
 */
export const setStorageItem = (key: string, value: string, storageType: StorageType = 'session'): void => {
  if (typeof window === 'undefined') return;

  try {
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    storage.setItem(key, value);
  } catch (error) {
    logApp.error(`Failed to set item in ${storageType}Storage:`, error);
  }
};

/**
 * Removes a value from storage
 * @param key - Storage key
 * @param storageType - Type of storage to use (default: session for security)
 */
export const removeStorageItem = (key: string, storageType: StorageType = 'session'): void => {
  if (typeof window === 'undefined') return;

  try {
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    storage.removeItem(key);
  } catch (error) {
    logApp.error(`Failed to remove item from ${storageType}Storage:`, error);
  }
};

/**
 * Storage keys used in the application
 */
export const STORAGE_KEYS = {
  USER: 'caseflow.user',                    // User identifier - sessionStorage
  CASE_UPDATED: 'caseflow.caseUpdated',     // Cross-tab sync timestamp - localStorage
  FILTERS: {
    UNOS_ID: 'caseflow.filters.unosId',
    LOCATION: 'caseflow.filters.location',
    STATUS: 'caseflow.filters.status',
    SERVICE_TYPE: 'caseflow.filters.serviceType',
    DATE_RANGE: 'caseflow.filters.dateRange',
    SHOW_BILLED: 'caseflow.filters.showBilled',
  },
  DEBUG: {
    MASTER: 'caseflow.debug',
    APP: 'caseflow.debug.app',
    API: 'caseflow.debug.api',
  },
} as const;
